/**
 * Just enough music theory to make invalid output unrepresentable.
 *
 * This is the load-bearing idea of the whole project. The original generator
 * writes every note by hand, which is why it cannot be randomised: nothing in a
 * list of note names knows which notes were allowed. Move the description up a
 * level, to a key and a progression, and a random choice inside it is still
 * music.
 *
 * Same principle as a golden set's must-include claims, or the Article 50
 * checker: describe what has to stay true, and let the thing that renders it
 * choose the specifics. A format you can safely randomise is one where being
 * wrong is not expressible.
 */

const CHROMATIC = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

/** Pitch classes, so a key can be named the way a person would say it. */
const PITCH_CLASS: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5,
  "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
};

export type Mode = "major" | "minor" | "dorian" | "phrygian" | "lydian" | "mixolydian";

/** Semitone offsets from the tonic. Modes carry mood, so they are the format's mood dial. */
const MODES: Record<Mode, number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
};

/** Every mode and every tonic, so the editor can offer them rather than
 *  making someone type a key and find out later whether it exists. */
export const MODE_NAMES = Object.keys(MODES) as Mode[];
export const TONIC_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

export interface Key {
  tonic: string;
  mode: Mode;
}

export function parseKey(text: string): Key {
  const [tonic, modeName = "major"] = text.trim().split(/\s+/);
  const mode = modeName.toLowerCase() as Mode;
  if (!(tonic in PITCH_CLASS)) throw new Error(`unknown tonic: ${tonic}`);
  if (!(mode in MODES)) throw new Error(`unknown mode: ${modeName}`);
  return { tonic, mode };
}

/** Roman numeral degrees, upper case for major triads, lower for minor. */
const NUMERALS = ["i", "ii", "iii", "iv", "v", "vi", "vii"];

export interface Degree {
  /** 0-indexed scale degree. */
  index: number;
  /** true when the numeral was written upper case, forcing a major third. */
  forcedMajor: boolean;
  /** true when the numeral was written lower case, forcing a minor third. */
  forcedMinor: boolean;
  seventh: boolean;
}

/**
 * Parse one roman numeral. Case carries meaning, as it does on paper: `VI` in a
 * minor key is the natural major sixth, `vi` would be something else, and a
 * format that ignored the difference would quietly flatten every progression
 * into the same colour.
 */
export function parseDegree(text: string): Degree {
  const raw = text.trim();
  const seventh = raw.endsWith("7");
  const body = seventh ? raw.slice(0, -1) : raw;
  const index = NUMERALS.indexOf(body.toLowerCase());
  if (index === -1) throw new Error(`unknown roman numeral: ${text}`);
  const isUpper = body === body.toUpperCase();
  return { index, forcedMajor: isUpper, forcedMinor: !isUpper, seventh };
}

export function parseProgression(text: string): Degree[] {
  return text.split(/[-–|]/).map((part) => part.trim()).filter(Boolean).map(parseDegree);
}

/** Absolute semitone for a scale degree in an octave, relative to C0 = 0. */
function degreeSemitone(key: Key, degreeIndex: number, octave: number): number {
  const intervals = MODES[key.mode];
  const wrapped = ((degreeIndex % 7) + 7) % 7;
  const octaveShift = Math.floor(degreeIndex / 7);
  return PITCH_CLASS[key.tonic] + intervals[wrapped] + (octave + octaveShift) * 12;
}

function semitoneToNote(semi: number): string {
  const octave = Math.floor(semi / 12);
  const pc = ((semi % 12) + 12) % 12;
  return `${CHROMATIC[pc]}${octave}`;
}

/**
 * Build the notes of a triad (or seventh) for a degree, in a given octave.
 *
 * Thirds are stacked within the mode rather than forced to a template, so a
 * minor key's chords come out minor without anyone writing them down. The
 * numeral's case overrides the third when it disagrees, which is how borrowed
 * chords stay expressible.
 */
export function chordNotes(key: Key, degree: Degree, octave: number): string[] {
  const root = degreeSemitone(key, degree.index, octave);
  let third = degreeSemitone(key, degree.index + 2, octave);
  const fifth = degreeSemitone(key, degree.index + 4, octave);

  const thirdInterval = third - root;
  if (degree.forcedMajor && thirdInterval === 3) third += 1;
  if (degree.forcedMinor && thirdInterval === 4) third -= 1;

  const notes = [root, third, fifth];
  if (degree.seventh) notes.push(degreeSemitone(key, degree.index + 6, octave));
  return notes.map(semitoneToNote);
}

/** The single note a bass line would play for a degree. */
export function bassNote(key: Key, degree: Degree, octave: number): string {
  return semitoneToNote(degreeSemitone(key, degree.index, octave));
}

/**
 * Every note of the scale within an inclusive octave range.
 *
 * This is the pool a melody or rhythm figure draws from, and it is the reason
 * randomising cannot produce a wrong note: notes outside the key are not in the
 * bag to be picked.
 */
export function scaleNotes(key: Key, fromOctave: number, toOctave: number): string[] {
  const out: string[] = [];
  for (let octave = fromOctave; octave <= toOctave; octave++) {
    for (let d = 0; d < 7; d++) out.push(semitoneToNote(degreeSemitone(key, d, octave)));
  }
  return out;
}

/**
 * The notes of a chord, plus the rest of the scale, ordered so that chord tones
 * come first.
 *
 * A figure that lands mostly on chord tones sounds deliberate; one that lands
 * anywhere sounds like an accident. Weighting the pool is cheaper and more
 * robust than trying to write rules about passing notes.
 */
export function chordToneBias(key: Key, degree: Degree, fromOctave: number, toOctave: number): {
  chordTones: string[];
  others: string[];
} {
  const pool = scaleNotes(key, fromOctave, toOctave);
  const chordPitchClasses = new Set(
    chordNotes(key, degree, 3).map((n) => n.replace(/\d+$/, "")),
  );
  const chordTones = pool.filter((n) => chordPitchClasses.has(n.replace(/\d+$/, "")));
  const others = pool.filter((n) => !chordPitchClasses.has(n.replace(/\d+$/, "")));
  return { chordTones, others };
}
